// src/forms/DeleteItemDialog.jsx
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";

export default function DeleteItemDialog({ open, onOpenChange, item, itemLabel = "item", onConfirm, loading = false }) {
  const [localLoading, setLocalLoading] = useState(false);
   const [confirmText, setConfirmText] = useState("");

  const isLoading = loading || localLoading;

  const itemName = useMemo(() => {
    return (
      item?.name ||
      item?.email ||
      item?.username ||
      item?.role ||
      item?.title ||
      itemLabel
    );
  }, [item, itemLabel]);

  const needsConfirmation = ["pathways", "roles", "users"].includes(itemLabel);

  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setLocalLoading(false);
      return;
    }
    // Dialog opened or item changed: require fresh confirmation input
    setConfirmText("");
  }, [open, itemName]);

  const handleConfirm = async () => {
    // Provide local feedback in case parent forgot to pass loading
    if (!onConfirm) return;
    try {
      setLocalLoading(true);
      await onConfirm(item);
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-auto">
        <DialogHeader className="">
          <DialogTitle className="font-heading text-(--neutral-900)">Delete</DialogTitle>
        </DialogHeader>

        <div className="">
          <p className="text-sm text-(--neutral-900) mb-4 font-body">
            Are you sure you want to delete{" "}
            <span className="font-extrabold text-(--neutral-900) mr-1">
              {itemName}
            </span>
            ? This action cannot be undone.
          </p>
          {(itemLabel === "pathways" || itemLabel === "roles" || itemLabel === "users") && (
            <p className="text-(--color-primary) font-bold mt-2">
              {itemLabel === "users" ? (
                <>
                  Warning: Deleting this user can remove their access to the platform and may affect any roles, enrollments, submissions, progress, or audit history linked to their account.
                  <br />
                  Ensure you really intend to delete this user and that any required reassignment or data retention steps have been handled before proceeding.
                </>
              ) : (
                <>
                  Warning: Deleting {itemLabel === "pathways" ? "this pathway" : "this role"} will also affect all users assigned to it.
                  <br />
                  This may remove their access or progress. Please ensure you have re-assigned or informed affected users before proceeding.
                </>
              )}
            </p>
          )}
          {needsConfirmation && (
            <div className="mt-4">
              <label className="font-body text-sm text-(--neutral-900)">
                Type <b>{itemName}</b> to confirm:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                className="border rounded px-2 py-1 w-full mt-1 text-(--neutral-700)"
                placeholder={`Type "${itemName}"`}
              />
            </div>
          )}
        </div>

        <DialogFooter className="">
          <DialogClose asChild>
            <Button
              variant="outline"
              type="button"
              disabled={isLoading}
              className="">
              Cancel
            </Button>
          </DialogClose>

          <Button
            type="button"
            variant=""
            className=""
            onClick={handleConfirm}
            loading={isLoading}
            disabled={
              isLoading ||
              (needsConfirmation && confirmText !== itemName)
            }
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
